import { Injectable, NgZone } from '@angular/core';
import { environment } from '../../../environments/environment';
import { Store } from '@ngxs/store';
import { ActivatedRoute } from '@angular/router';
import { TemplateConfig } from '../dto/response/template.config';
import { UpdateTemplateConfig } from '../store/actions/templateConfig.action';
import { doc, docData, Firestore, DocumentData } from '@angular/fire/firestore';
import { skip, distinctUntilChanged, filter, map } from 'rxjs';

interface DraftConfigData extends DocumentData {
  config?: string;
  lastEventId?: string;
}

interface DraftScrollData extends DocumentData {
  sectionId?: string;
  lastEventId?: string;
}

@Injectable({
  providedIn: 'root'
})
export class DraftRtConfigService {

  private elementFocused: HTMLElement | null = null;

  constructor(
    private ngZone: NgZone,
    private store: Store,
    private route: ActivatedRoute,
    private firestore: Firestore
  ) { }

  init(siteId: string) {
    this.initTemplateConfigEditor(siteId);
    this.initScrollAndFocus(siteId);
  }

  private initScrollAndFocus(siteId: string) {
    if (this.route.snapshot.queryParams['template-status'] != 'DRAFT') {
      return;
    }

    const ref = doc(this.firestore, `store/${siteId}/template/templateId/scroll/draft`);

    docData(ref).pipe(
      skip(1),                                                          // Ignora snapshot inicial
      map(data => data as DraftScrollData),                             // Typing
      distinctUntilChanged((prev, curr) => prev?.lastEventId === curr?.lastEventId), // Previene duplicados
      filter((data) => !!data?.sectionId)                               // Solo procesa si hay sectionId
    ).subscribe((data) => {
      console.log('[DraftRT Scroll] Cambio real recibido:', data.lastEventId);
      this.ngZone.run(() => {
        this.scrollToSection(data);
      });
    });
  }

  private scrollToSection(scrollTo: { sectionId?: string }) {
    if (!scrollTo.sectionId) return;

    this.ngZone.runOutsideAngular(() => {
      if (this.elementFocused) {
        this.elementFocused.classList.remove('highlight-section');
      }
      this.elementFocused = document.getElementById(scrollTo.sectionId!);
      if (this.elementFocused) {
        console.log('[DraftRT Scroll] Aplicando scroll a:', scrollTo.sectionId);
        this.elementFocused.scrollIntoView({ behavior: 'smooth', block: 'center' });
        this.elementFocused.classList.add('highlight-section');
        setTimeout(() => {
          if (this.elementFocused) {
            this.elementFocused.classList.remove('highlight-section');
          }
        }, 2000);
      }
    });
  }

  private initTemplateConfigEditor(siteId: string) {
    if (this.route.snapshot.queryParams['template-status'] != 'DRAFT') {
      return;
    }

    const ref = doc(this.firestore, `store/${siteId}/template/${environment.TEMPLATE_BASE_ID}/config/draft`);

    docData(ref).pipe(
      skip(1),                                                          // Ignora snapshot inicial
      map(data => data as DraftConfigData),                             // Typing
      distinctUntilChanged((prev, curr) => prev?.lastEventId === curr?.lastEventId), // Previene duplicados
      filter((data) => !!data?.config)                                  // Solo procesa si hay config
    ).subscribe((data) => {
      console.log('[DraftRT Config] Cambio real recibido:', data.lastEventId);
      this.ngZone.run(() => {
        const update: TemplateConfig = JSON.parse(data.config!);
        this.store.dispatch(new UpdateTemplateConfig(update));
      });
    });
  }
}
